import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Badge, Button, Input, Modal } from "../../../../shared";
import {
  useCreateCustomerApiKeyMutation,
  useGetCustomerApiKeyQuery,
  useRevokeCustomerApiKeyMutation,
} from "../../../../redux/services/customers/customersApi";
import { formatDate } from "../../../../utils";

const ApiKeyModal = ({ customerId, visible, setVisible }) => {
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const { data: apiKeyData, isFetching, refetch } = useGetCustomerApiKeyQuery(
    { id: customerId },
    { skip: !visible || !customerId }
  );
  const [createCustomerApiKey, { isLoading: isCreating }] =
    useCreateCustomerApiKeyMutation();
  const [revokeCustomerApiKey, { isLoading: isRevoking }] =
    useRevokeCustomerApiKeyMutation();

  const apiKeyInfo = apiKeyData?.data || {};
  const hasApiKey = Boolean(apiKeyInfo?.enabled);
  const createdAtLabel = useMemo(() => {
    if (!apiKeyInfo?.createdAt) {
      return "Not generated yet";
    }

    return formatDate(apiKeyInfo.createdAt, false, false, true) || "Not generated yet";
  }, [apiKeyInfo]);

  async function handleGenerateApiKey() {
    if (!customerId) {
      return;
    }

    try {
      const response = await createCustomerApiKey({ id: customerId }).unwrap();
      setGeneratedApiKey(response?.data?.apiKey || "");
      toast(response?.message || "Customer API key generated successfully", {
        type: "success",
        toastId: "customer-api-key-generated",
      });
      refetch();
    } catch (error) {
      toast(error?.data?.message || "Unable to generate customer API key", {
        type: "error",
        toastId: "customer-api-key-generated-error",
      });
    }
  }

  async function handleRevokeApiKey() {
    if (!customerId) {
      return;
    }

    try {
      const response = await revokeCustomerApiKey({ id: customerId }).unwrap();
      setGeneratedApiKey("");
      toast(response?.message || "Customer API key revoked successfully", {
        type: "success",
        toastId: "customer-api-key-revoked",
      });
      refetch();
    } catch (error) {
      toast(error?.data?.message || "Unable to revoke customer API key", {
        type: "error",
        toastId: "customer-api-key-revoked-error",
      });
    }
  }

  async function handleCopyApiKey() {
    if (!generatedApiKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedApiKey);
      toast("API key copied to clipboard", {
        type: "success",
        toastId: "customer-api-key-copied",
      });
    } catch (_error) {
      toast("Unable to copy API key to clipboard", {
        type: "error",
        toastId: "customer-api-key-copy-error",
      });
    }
  }

  const content = isFetching ? (
    <div className="py-8 text-center">Loading...</div>
  ) : (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-medium text-gray-900">API Access</p>
          <p className="text-sm text-gray-500 mt-1">
            Generate a customer API key for integrations. The full key is only shown once.
          </p>
        </div>
        <Badge color={hasApiKey ? "green" : "gray"} label={hasApiKey ? "Enabled" : "Disabled"} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <p className="text-sm font-medium text-gray-900">Current Preview</p>
          <p className="text-sm text-gray-500 mt-1">{apiKeyInfo?.preview || "No API key generated"}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">Last Generated</p>
          <p className="text-sm text-gray-500 mt-1">{createdAtLabel}</p>
        </div>
      </div>

      {generatedApiKey ? (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">Copy this key now</p>
          <p className="text-sm text-amber-800">
            This is the only time the full API key will be shown.
          </p>
          <Input
            id="generatedApiKey"
            label="Generated API Key"
            value={generatedApiKey}
            readOnly
            onChange={() => {}}
          />
          <div className="flex justify-end">
            <Button type="alternative" onClick={handleCopyApiKey}>
              Copy key
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button
          type="alternative"
          disabled={!hasApiKey}
          loading={isRevoking}
          onClick={handleRevokeApiKey}
        >
          Revoke key
        </Button>
        <Button loading={isCreating} onClick={handleGenerateApiKey}>
          {hasApiKey ? "Regenerate key" : "Generate key"}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      title="Customer API Key"
      size="lg"
      hideFooter
      content={content}
    />
  );
};

export default ApiKeyModal;
